import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { 
  BarChart3, 
  Map, 
  Users, 
  Shield, 
  ArrowRight, 
  CheckCircle,
  TrendingUp,
  Building,
  GraduationCap,
  Heart,
  Zap,
  Droplets,
  Bus,
  Construction,
  Route,
  Stethoscope,
  Trash2,
  Home,
  Store,
  MapPin,
  Info,
  FileText,
  Globe,
  Menu,
  X,
  User,
  LogIn,
  Mail,
  Plus
} from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import { CountryFlag } from '../components/CountryFlag';
import { useLanguage } from '../contexts/LanguageContext';
import { useCountry } from '../contexts/CountryContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const { t, language } = useLanguage();
  const { country, countries, selectCountry, countryConfig, configLoading } = useCountry();
  const [stats, setStats] = useState({
    total_surveys: 0,
    total_responses: 0,
    states_covered: 36
  });
  const [activeSurveys, setActiveSurveys] = useState([]);
  const [governanceData, setGovernanceData] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Get current country info with stats
  const currentCountry = countries.find(c => c.code === country) || { name: 'Nigeria', code: 'NG' };
  
  // Dynamic country statistics (fallback to Nigeria if not available)
  const countryStats = countryConfig?.stats || {
    level1_count: 37,
    level1_label: 'States & FCT',
    level2_count: 774,
    level2_label: 'LGAs'
  };

  // Handle country change from dropdown
  const handleCountryChange = async (code) => {
    await selectCountry(code);
  };

  useEffect(() => {
    fetchStats();
    fetchGovernanceData();
  }, []);

  // Timeline grouping is country-specific
  useEffect(() => {
    fetchTimeline();
  }, [country]);

  const fetchTimeline = async () => {
    try {
      const response = await fetch(`${API}/surveys/timeline/grouped?country=${country || 'NG'}`);
      if (response.ok) {
        const data = await response.json();
        const tl = data.timeline || [];
        setTimeline(tl);
        const years = tl.map(t => t.year);
        setAvailableYears(years);
        setSelectedYear(prev => (prev && years.includes(prev)) ? prev : (years[0] || new Date().getFullYear()));
      }
    } catch (error) {
      console.error('Error fetching timeline:', error);
    }
  };

  const fetchGovernanceData = async () => {
    try {
      const response = await fetch(`${API}/nigeria/governance-responsibilities`);
      if (response.ok) {
        const data = await response.json();
        setGovernanceData(data);
      }
    } catch (error) {
      console.error('Error fetching governance data:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/analytics/overview`);
      if (response.ok) {
        const data = await response.json();
        setStats({
          total_surveys: data.total_surveys || 0,
          total_responses: data.total_responses || 0,
          states_covered: data.top_states?.length || 36
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchActiveSurveys = async () => {
    try {
      const params = ['active_only=true', `country=${country || 'NG'}`];
      if (selectedYear) params.push(`year=${selectedYear}`);
      if (selectedQuarter) params.push(`quarter=${selectedQuarter}`);
      if (selectedMonth) params.push(`month=${selectedMonth}`);
      const response = await fetch(`${API}/surveys?${params.join('&')}`);
      if (response.ok) {
        const data = await response.json();
        setActiveSurveys(data);
      }
    } catch (error) {
      console.error('Error fetching surveys:', error);
    }
  };

  // Re-fetch surveys when country / year / quarter / month changes
  useEffect(() => {
    if (selectedYear) {
      fetchActiveSurveys();
    }
  }, [country, selectedYear, selectedQuarter, selectedMonth]);

  // Handlers that keep the drill-down consistent
  const handleSelectYear = (yr) => {
    setSelectedYear(yr);
    setSelectedQuarter(null);
    setSelectedMonth(null);
  };
  const handleSelectQuarter = (q) => {
    setSelectedQuarter(prev => (prev === q ? null : q));
    setSelectedMonth(null);
  };
  const handleSelectMonth = (m) => {
    setSelectedMonth(prev => (prev === m ? null : m));
  };

  const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const currentYearNode = timeline.find(t => t.year === selectedYear);
  const yearQuarters = currentYearNode ? currentYearNode.quarters : [];
  const visibleMonths = selectedQuarter
    ? (yearQuarters.find(q => q.quarter === selectedQuarter)?.months || [])
    : yearQuarters.flatMap(q => q.months);

  const categoryIcons = {
    economy: TrendingUp,
    security: Shield,
    education: GraduationCap,
    health: Heart,
    infrastructure: Building,
    youth: Users,
    women: Users,
    jobs: Users
  };

  const issueIcons = {
    Stethoscope,
    GraduationCap,
    Route,
    Construction,
    Bus,
    Zap,
    Droplets,
    Trash2,
    Shield,
    Store,
    MapPin,
    Home,
    TrendingUp,
    Heart,
    Building
  };

  const getLevelBadge = (level) => {
    const colors = {
      federal: 'bg-red-100 text-red-800 border-red-200',
      state: 'bg-blue-100 text-blue-800 border-blue-200',
      local: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const filteredGovernance = selectedLevel === 'all' 
    ? governanceData 
    : governanceData.filter(item => item.levels.includes(selectedLevel));

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded flex items-center justify-center">
                <span className="text-white font-bold text-sm">CV</span>
              </div>
              <span className="font-bold text-lg tracking-tight">Citizen Voice</span>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              <Link to="/analytics" className="text-sm text-muted-foreground hover:text-foreground transition-colors px-2">
                {t('nav_analytics', 'Analytics')}
              </Link>
              <Link to="/map" className="text-sm text-muted-foreground hover:text-foreground transition-colors px-2">
                {t('nav_map', 'Map')}
              </Link>
              <Link to="/reports/purchase" className="text-sm text-muted-foreground hover:text-foreground transition-colors px-2">
                <FileText className="h-4 w-4 inline mr-1" />
                {language === 'fr' ? 'Rapports' : 'Reports'}
              </Link>
              
              {/* Create Survey Button */}
              <Link to="/create-survey">
                <Button variant="default" size="sm" className="bg-primary hover:bg-primary/90">
                  <Plus className="h-4 w-4 mr-1" />
                  {language === 'fr' ? 'Créer' : 'Create Survey'}
                </Button>
              </Link>

              {/* Country Selector Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2" disabled={configLoading}>
                    {configLoading ? (
                      <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      <CountryFlag code={currentCountry.code} width={22} />
                    )}
                    <span className="hidden lg:inline text-sm">{currentCountry.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 max-h-72 overflow-y-auto">
                  {/* Featured countries first */}
                  {['NG', 'GH', 'KE', 'ZA', 'EG', 'ET', 'TZ', 'UG', 'SN', 'CM'].map((code) => {
                    const c = countries.find(x => x.code === code);
                    if (!c) return null;
                    return (
                      <DropdownMenuItem 
                        key={c.code} 
                        onClick={() => handleCountryChange(c.code)}
                        className={c.code === country ? 'bg-primary/10' : ''}
                      >
                        <CountryFlag code={c.code} width={20} className="mr-2" />
                        {language === 'fr' ? c.name_fr : c.name}
                        {c.code === country && <span className="ml-auto text-primary">✓</span>}
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/select-country" className="w-full">
                      <Globe className="h-4 w-4 mr-2" />
                      {language === 'fr' ? 'Tous les pays' : 'All Countries'}
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Language Selector */}
              <LanguageSelector variant="ghost" showLabel={false} />
              
              {/* Login Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <User className="h-4 w-4" />
                    <span className="hidden lg:inline">{language === 'fr' ? 'Connexion' : 'Login'}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/admin/login" className="w-full cursor-pointer">
                      <LogIn className="h-4 w-4 mr-2" />
                      {language === 'fr' ? 'Google Admin' : 'Google Admin'}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/create-survey" className="w-full cursor-pointer">
                      <Mail className="h-4 w-4 mr-2" />
                      {language === 'fr' ? 'Email Abonné' : 'Subscriber Email'}
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2">
              <LanguageSelector variant="ghost" showLabel={false} />
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-white">
            <div className="px-4 py-4 space-y-3">
              <Link 
                to="/analytics" 
                className="block px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                <BarChart3 className="h-4 w-4 inline mr-2" />
                {t('nav_analytics', 'Analytics')}
              </Link>
              <Link 
                to="/map" 
                className="block px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Map className="h-4 w-4 inline mr-2" />
                {t('nav_map', 'Map')}
              </Link>
              <Link 
                to="/reports/purchase" 
                className="block px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                <FileText className="h-4 w-4 inline mr-2" />
                {language === 'fr' ? 'Rapports' : 'Reports'}
              </Link>
              <Link 
                to="/create-survey" 
                className="block px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Plus className="h-4 w-4 inline mr-2" />
                {language === 'fr' ? 'Créer une Enquête' : 'Create a Survey'}
              </Link>
              <div className="border-t pt-3 mt-3">
                <p className="px-3 text-xs text-muted-foreground mb-2">{language === 'fr' ? 'Connexion' : 'Login'}</p>
                <Link 
                  to="/admin/login" 
                  className="block px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <LogIn className="h-4 w-4 inline mr-2" />
                  {language === 'fr' ? 'Google Admin' : 'Google Admin Login'}
                </Link>
                <Link 
                  to="/create-survey" 
                  className="block px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Mail className="h-4 w-4 inline mr-2" />
                  {language === 'fr' ? 'Email Abonné' : 'Subscriber Email Login'}
                </Link>
              </div>
              <div className="border-t pt-3 mt-3">
                <p className="px-3 text-xs text-muted-foreground mb-2">{language === 'fr' ? 'Pays' : 'Country'}</p>
                <Link 
                  to="/select-country" 
                  className="block px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <CountryFlag code={currentCountry.code} width={20} className="mr-2" />
                  {currentCountry.name} ({language === 'fr' ? 'Changer' : 'Change'})
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-50 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <Badge variant="secondary" className="mb-4">
                Non-Partisan Civic Platform
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground mb-6">
                {language === 'fr' ? 'Votre Voix,' : 'Your Voice,'}
                <span className="text-primary"> {language === 'fr' ? "Gouvernance Basée sur les Données" : "Data Driven Governance"}</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-lg leading-relaxed">
                {t('hero_subtitle', 'Participate in governance surveys, share your concerns, and see how citizens feel about the issues that matter most.')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                {activeSurveys.length > 0 ? (
                  <Link to={`/survey/${activeSurveys[0].survey_id}`}>
                    <Button size="lg" className="bg-primary hover:bg-primary/90 text-white shadow-lg" data-testid="take-survey-btn">
                      {language === 'fr' ? 'Répondre à une Enquête' : 'Take a Survey'}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                ) : (
                  <Link to="/analytics">
                    <Button size="lg" className="bg-primary hover:bg-primary/90 text-white shadow-lg" data-testid="take-survey-btn">
                      {language === 'fr' ? 'Voir les Enquêtes' : 'Browse Surveys'}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                )}
                <Link to="/analytics">
                  <Button size="lg" variant="outline" className="border-2 hover:bg-muted shadow-md" data-testid="view-results-btn">
                    {language === 'fr' ? 'Voir les Résultats' : 'View Results'}
                    <BarChart3 className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/create-survey">
                  <Button size="lg" variant="secondary" className="shadow-md" data-testid="create-survey-hero-btn">
                    <Plus className="mr-2 h-4 w-4" />
                    {language === 'fr' ? 'Créer une Enquête' : 'Create Survey'}
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative animate-slide-up stagger-2">
              <div className="relative rounded-lg overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1489392191049-fc10c97e64b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3wxMjA3fDB8MXxzZWFyY2h8Mnx8YWZyaWNhJTIwbWFwfGVufDB8fHx8MTcwMjMwMDAwMHww&ixlib=rb-4.0.3&q=85&w=800"
                  alt={`${currentCountry.name} - Africa Map`}
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent"></div>
                {/* Country indicator overlay */}
                <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg">
                  <div className="flex items-center gap-2">
                    <CountryFlag code={currentCountry.code} width={28} />
                    <span className="font-semibold text-foreground">{currentCountry.name}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Dynamic based on country */}
      <section className="bg-foreground text-background py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="animate-slide-up stagger-1">
              <div className="text-4xl md:text-5xl font-extrabold text-secondary mb-2">
                {countryStats.level1_count}
              </div>
              <div className="text-sm uppercase tracking-wider text-background/70">
                {countryStats.level1_label}
              </div>
            </div>
            <div className="animate-slide-up stagger-2">
              <div className="text-4xl md:text-5xl font-extrabold text-secondary mb-2">
                {countryStats.level2_count.toLocaleString()}
              </div>
              <div className="text-sm uppercase tracking-wider text-background/70">
                {countryStats.level2_label}
              </div>
            </div>
            <div className="animate-slide-up stagger-3">
              <div className="text-4xl md:text-5xl font-extrabold text-secondary mb-2">
                {stats.total_responses.toLocaleString()}
              </div>
              <div className="text-sm uppercase tracking-wider text-background/70">
                {language === 'fr' ? 'Réponses' : 'Responses'}
              </div>
            </div>
            <div className="animate-slide-up stagger-4">
              <div className="text-4xl md:text-5xl font-extrabold text-secondary mb-2">1</div>
              <div className="text-sm uppercase tracking-wider text-background/70">
                {language === 'fr' ? 'Voix' : 'Voice'}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">How It Works</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              A transparent platform that connects citizen sentiment with governance insights.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="card-hover">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Participate Anonymously</h3>
                <p className="text-muted-foreground">
                  Share your views on governance issues without revealing your identity. Your privacy is protected.
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Real-Time Analytics</h3>
                <p className="text-muted-foreground">
                  Watch as citizen responses aggregate into meaningful insights with live charts and statistics.
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Map className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">
                  {language === 'fr' ? 'Carte Interactive' : 'Interactive Map'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'fr' 
                    ? `Explorez le sentiment à travers les ${countryStats.level1_count} ${countryStats.level1_label.toLowerCase()} et ${countryStats.level2_count} ${countryStats.level2_label.toLowerCase()} de ${currentCountry.name}.`
                    : `Explore sentiment across ${currentCountry.name}'s ${countryStats.level1_count} ${countryStats.level1_label.toLowerCase()} and ${countryStats.level2_count} ${countryStats.level2_label.toLowerCase()} with our election-style map view.`
                  }
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Active Surveys Section */}
      {availableYears.length > 0 && (
        <section className="py-16 bg-muted/30" id="surveys">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold">
                  {language === 'fr' ? `Enquêtes ${currentCountry.name} ${selectedYear}` : `${currentCountry.name} ${selectedYear} Surveys`}
                </h2>
                <p className="text-muted-foreground mt-1">
                  {language === 'fr' 
                    ? `Participez aux enquêtes sur les politiques de ${currentCountry.name}` 
                    : `Participate in ${currentCountry.name} policy surveys`}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="text-primary border-primary">
                  {activeSurveys.length} {language === 'fr' ? 'Ouvertes' : 'Open'}
                </Badge>
                <Badge variant="secondary" className="gap-1.5">
                  <CountryFlag code={currentCountry.code} width={16} /> {selectedYear}
                </Badge>
              </div>
            </div>

            {/* Timeline selector: Year > Quarter > Month */}
            <div className="mb-6 space-y-3" data-testid="timeline-selector">
              {/* Year row */}
              <div className="flex flex-wrap items-center gap-2" data-testid="year-timeline">
                <span className="text-sm font-medium text-muted-foreground w-16">
                  {language === 'fr' ? 'Année' : 'Year'}
                </span>
                {availableYears.map((yr) => {
                  const isCurrent = yr === availableYears[0];
                  const isSelected = yr === selectedYear;
                  return (
                    <button
                      key={yr}
                      type="button"
                      onClick={() => handleSelectYear(yr)}
                      data-testid={`year-tab-${yr}`}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                        isSelected
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-foreground border-border hover:border-primary'
                      }`}
                    >
                      {yr} {isCurrent
                        ? `(${language === 'fr' ? 'En cours' : 'Current'})`
                        : `(${language === 'fr' ? 'Archivé' : 'Past'})`}
                    </button>
                  );
                })}
              </div>

              {/* Quarter row */}
              {yearQuarters.length > 0 && (
                <div className="flex flex-wrap items-center gap-2" data-testid="quarter-timeline">
                  <span className="text-sm font-medium text-muted-foreground w-16">
                    {language === 'fr' ? 'Trimestre' : 'Quarter'}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleSelectQuarter(null)}
                    data-testid="quarter-tab-all"
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                      !selectedQuarter ? 'bg-secondary text-secondary-foreground border-secondary' : 'bg-background text-foreground border-border hover:border-primary'
                    }`}
                  >
                    {language === 'fr' ? 'Tous' : 'All'}
                  </button>
                  {yearQuarters.map((q) => (
                    <button
                      key={q.quarter}
                      type="button"
                      onClick={() => handleSelectQuarter(q.quarter)}
                      data-testid={`quarter-tab-${q.quarter}`}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                        selectedQuarter === q.quarter ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground border-border hover:border-primary'
                      }`}
                    >
                      Q{q.quarter} <span className="opacity-70">({q.count})</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Month row */}
              {visibleMonths.length > 0 && (
                <div className="flex flex-wrap items-center gap-2" data-testid="month-timeline">
                  <span className="text-sm font-medium text-muted-foreground w-16">
                    {language === 'fr' ? 'Mois' : 'Month'}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleSelectMonth(null)}
                    data-testid="month-tab-all"
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                      !selectedMonth ? 'bg-secondary text-secondary-foreground border-secondary' : 'bg-background text-foreground border-border hover:border-primary'
                    }`}
                  >
                    {language === 'fr' ? 'Tous' : 'All'}
                  </button>
                  {visibleMonths.map((m) => (
                    <button
                      key={m.month}
                      type="button"
                      onClick={() => handleSelectMonth(m.month)}
                      data-testid={`month-tab-${m.month}`}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                        selectedMonth === m.month ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground border-border hover:border-primary'
                      }`}
                    >
                      {MONTH_NAMES[m.month - 1]} <span className="opacity-70">({m.count})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {activeSurveys.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="no-surveys-year">
                {language === 'fr'
                  ? `Aucune enquête disponible pour cette période.`
                  : `No surveys available for this period.`}
              </div>
            ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {activeSurveys.map((survey) => {
                const IconComponent = categoryIcons[survey.policy_category] || Users;
                const categoryLabels = {
                  economy: { en: 'Economy', fr: 'Économie' },
                  security: { en: 'Security', fr: 'Sécurité' },
                  education: { en: 'Education', fr: 'Éducation' },
                  health: { en: 'Health', fr: 'Santé' },
                  infrastructure: { en: 'Infrastructure', fr: 'Infrastructure' },
                  youth: { en: 'Youth', fr: 'Jeunesse' },
                  women: { en: 'Women', fr: 'Femmes' },
                  jobs: { en: 'Jobs', fr: 'Emploi' }
                };
                const categoryLabel = categoryLabels[survey.policy_category] || { en: survey.policy_category, fr: survey.policy_category };
                
                return (
                  <Card key={survey.survey_id} className="card-hover group" data-testid={`survey-card-${survey.survey_id}`}>
                    <CardContent className="pt-5 pb-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                          <IconComponent className="h-5 w-5 text-primary" />
                        </div>
                        <Badge variant="outline" className="text-xs capitalize">
                          {survey.governance_level}
                        </Badge>
                      </div>
                      <h3 className="text-base font-semibold mb-1 line-clamp-1">
                        {language === 'fr' ? categoryLabel.fr : categoryLabel.en}
                      </h3>
                      <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                        {survey.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          {survey.response_count || 0} {language === 'fr' ? 'réponses' : 'responses'}
                        </span>
                        {selectedYear === availableYears[0] ? (
                          <Link to={`/survey/${survey.survey_id}`}>
                            <Button size="sm" className="h-7 text-xs bg-primary hover:bg-primary/90" data-testid={`take-survey-${survey.survey_id}`}>
                              {language === 'fr' ? 'Participer' : 'Take Survey'}
                              <ArrowRight className="ml-1 h-3 w-3" />
                            </Button>
                          </Link>
                        ) : (
                          <Link to={`/results/${survey.survey_id}`}>
                            <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`view-results-${survey.survey_id}`}>
                              {language === 'fr' ? 'Voir les résultats' : 'View Results'}
                              <BarChart3 className="ml-1 h-3 w-3" />
                            </Button>
                          </Link>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            )}
          </div>
        </section>
      )}

      {/* Governance Responsibility Guide */}
      <section className="py-16 md:py-24 bg-muted/30" id="governance-guide">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <Info className="h-3 w-3 mr-1" />
              Civic Education
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Who Handles Your Daily Issues?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Understanding which level of government is responsible helps you direct your concerns effectively.
            </p>
          </div>

          {/* Level Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <Button 
              variant={selectedLevel === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedLevel('all')}
              className="rounded-full"
            >
              All Issues
            </Button>
            <Button 
              variant={selectedLevel === 'federal' ? 'default' : 'outline'}
              onClick={() => setSelectedLevel('federal')}
              className={`rounded-full ${selectedLevel === 'federal' ? 'bg-red-600 hover:bg-red-700' : 'hover:bg-red-50 hover:text-red-700 hover:border-red-300'}`}
            >
              Federal
            </Button>
            <Button 
              variant={selectedLevel === 'state' ? 'default' : 'outline'}
              onClick={() => setSelectedLevel('state')}
              className={`rounded-full ${selectedLevel === 'state' ? 'bg-blue-600 hover:bg-blue-700' : 'hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300'}`}
            >
              State
            </Button>
            <Button 
              variant={selectedLevel === 'local' ? 'default' : 'outline'}
              onClick={() => setSelectedLevel('local')}
              className={`rounded-full ${selectedLevel === 'local' ? 'bg-green-600 hover:bg-green-700' : 'hover:bg-green-50 hover:text-green-700 hover:border-green-300'}`}
            >
              Local (LGA)
            </Button>
          </div>

          {/* Governance Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredGovernance.map((item) => {
              const IconComponent = issueIcons[item.icon] || Building;
              return (
                <Card key={item.id} className="card-hover group" data-testid={`governance-card-${item.id}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                        <IconComponent className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm mb-1">{item.issue}</h3>
                        <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                          {item.description}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {item.levels.map((level) => (
                            <span 
                              key={level} 
                              className={`text-xs px-2 py-0.5 rounded-full border capitalize ${getLevelBadge(level)}`}
                            >
                              {level}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-8 p-4 bg-white rounded-lg border">
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                <span>Federal Government</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                <span>State Government</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                <span>Local Government (LGA)</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1653669486779-671f54d26c13?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxOaWdlcmlhbiUyMGNpdGl6ZW5zJTIwZGl2ZXJzZSUyMGdyb3VwJTIwZGlzY3Vzc2lvbnxlbnwwfHx8d2hpdGV8MTc2NTg2NDI4OHww&ixlib=rb-4.1.0&q=85&w=600"
                alt="Nigerian Citizens in Discussion"
                className="rounded-lg shadow-lg"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
                Credible. Transparent. Non-Partisan.
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Anonymous Participation</h4>
                    <p className="text-sm text-muted-foreground">Your identity is never collected or stored.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Anti-Manipulation Safeguards</h4>
                    <p className="text-sm text-muted-foreground">Rate limiting and device tracking prevent ballot stuffing.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <BarChart3 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Open Data</h4>
                    <p className="text-sm text-muted-foreground">All aggregated results are publicly accessible.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Make Your Voice Heard</h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            Join thousands of Nigerians sharing their perspectives on governance and policy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {activeSurveys.length > 0 ? (
              <Link to={`/survey/${activeSurveys[0].survey_id}`}>
                <Button size="lg" variant="secondary" className="btn-active">
                  Start Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            ) : (
              <Button size="lg" variant="secondary" disabled>
                No Active Surveys
              </Button>
            )}
            <Link to="/map">
              <Button size="lg" variant="outline" className="bg-transparent text-white border-white hover:bg-white/10">
                Explore Map
                <Map className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-background/70 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-secondary rounded flex items-center justify-center">
                  <span className="text-foreground font-bold text-sm">CV</span>
                </div>
                <span className="font-bold text-lg text-background">Citizen Voice Nigeria</span>
              </div>
              <p className="text-sm max-w-md">
                A civic technology platform enabling Nigerian citizens to participate in governance surveys and view aggregated public sentiment data.
              </p>
              <p className="text-xs mt-4 text-background/50">
                This platform collects citizen sentiment data, not election results. All data is aggregated and anonymized.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-background mb-4">Platform</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/analytics" className="hover:text-background transition-colors">Analytics</Link></li>
                <li><Link to="/map" className="hover:text-background transition-colors">Map View</Link></li>
                <li><Link to="/admin/login" className="hover:text-background transition-colors">Admin</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-background mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><span className="cursor-pointer hover:text-background transition-colors">Privacy Policy</span></li>
                <li><span className="cursor-pointer hover:text-background transition-colors">Terms of Use</span></li>
                <li><span className="cursor-pointer hover:text-background transition-colors">Data Policy</span></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-background/10 mt-8 pt-8 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} Citizen Voice Nigeria. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
